/**
 * Represents resources from the /scheduled-events/ API collection.
 */

import { ActivatedRoute } from '@angular/router';

import { ScheduledEventsColumn } from './event-type';
import { loadBoolFilterFromGetParams, loadIntFilterFromGetParams, loadStringFilterFromGetParams } from '../utils';
import { EventTypeStatus, EventTypeWaitlistScope, ScheduleType } from './event-type-enums';
import { PaymentProcessorType } from './organization-config-enums';
import { MembershipInterval } from './membership-enums';
import { BookingStatus } from './booking-enums';
import { PaymentMethodType, PaymentType } from './payment-enums';

export enum ScheduledEventFilterBookingCapacity {
  full = 'full',
  notFull = 'not_full',
  halfOrMoreBooked = 'half_or_more_booked',
  lessThanHalfBooked = 'less_than_half_booked',
  atLeastOneBooking = 'at_least_one_booking',
  noBookings = 'no_bookings'
}

export enum ScheduledEventPaymentStatus {
  paymentPending = 'PEN',
  depositPaid = 'DEP',
  paymentComplete = 'COM',
  partiallyRefunded = 'PRF',
  refunded = 'REF',
  cancelled = 'CAN',
}

export enum WaitlistEntryStatus {
  waiting = 'WAI',
  pending = 'PEN',
  accepted = 'ACC',
  expired = 'EXP',
  rejected = 'REJ',
}

export enum ReservationBookingStatus {
  incomplete = 'INC',
  depositPaid = 'DEP',
  complete = 'COM',
  cancelled = 'CAN',
}

/**
 * The reason a reservation was cancelled. Mirrors the backend
 * Reservation.REASON_* constants (apps/event/models.py).
 */
export enum ReservationCancellationReason {
  stalePayment = 'stale_payment',
  stalePaymentDuplicate = 'stale_payment_duplicate',
  eventCancelled = 'event_cancelled',
  manual = 'manual',
  customerCancelled = 'customer_cancelled',
  associationJoinRequestDenied = 'association_join_request_denied',
}

/**
 * Get a human-readable version of a reservation's cancellation_reason value.
 */
export function getReservationCancellationReasonDisplay(reason: string): string {
  switch (reason) {
    case ReservationCancellationReason.stalePayment:
      return 'Non-payment';
    case ReservationCancellationReason.stalePaymentDuplicate:
      return 'Non-payment (duplicate booking)';
    case ReservationCancellationReason.eventCancelled:
      return 'Event cancelled';
    case ReservationCancellationReason.manual:
      return 'Cancelled by staff';
    case ReservationCancellationReason.customerCancelled:
      return 'Cancelled by customer';
    case ReservationCancellationReason.associationJoinRequestDenied:
      return 'Association join request denied';
    default:
      return 'Unknown';
  }
}

export interface ScheduledEventInstructor {
  id: number;
  first_name: string;
  last_name: string;
  is_sub: boolean;
}

export interface ScheduledEvent {
  event_part_id: number;
  event_id: number;
  event_type_id: number;
  event_type_status: EventTypeStatus;
  event_type_schedule_type: ScheduleType;
  roster_id: number | null;
  roster_name: string | null;
  start_time: string;
  end_time: string;
  start_datetime: string;
  end_datetime: string;
  time_zone: string;
  has_started: boolean;
  has_ended: boolean;
  name: string;
  occurrence_name: string;
  customer_facing_name: string;
  location_id: number;
  location_name: string;
  gym_area_id?: number;
  gym_area_name?: string;
  max_participant_count: number;
  event_scheduler_url: string;
  event_schedule_from_waitlist_url: string;
  event_waitlist_url: string;
  check_in_count: number;
  reservation_count: number;
  waitlist_scope: EventTypeWaitlistScope;
  waitlist_count: number;
  waitlist_count_pending: number;
  capacity: number;
  remaining_capacity: number;
  is_cancelled: boolean;
  is_private: boolean;
  reservations: ScheduledEventReservation[];
  waitlist_entries: WaitlistEntry[];
  event_part_number: number;
  event_part_count: number;
  instructors: ScheduledEventInstructor[];
  instructors_set_manually: boolean;
  schedule_id?: number;
  schedule_name?: string | null;
  schedule_exception_id?: number | null;
  schedule_exception_name?: string | null;
  allow_customers_to_join_waitlist: boolean;
  is_private_event_request_form: boolean;
  // The following fields are only present when retrieving an individual scheduled
  // event, for performance reasons. When listing scheduled events, they are null.
  num_notes: number | null;
  scheduled_events_columns: ScheduledEventsColumn[] | null;
  participant_agreement_template_id: number | null;
  participant_agreement_template_name: string | null;
  requires_waiver: boolean | null;
  climber_app_registration_url: string;
  // Default for the direct-booking modal's "includes check-in" checkbox: true
  // only when every fee on the event's pricing matrix includes entry
  // (PRO-1512). Null when listing.
  direct_booking_includes_entry_default: boolean | null;
}

export interface ScheduledEventReservation {
  reservation_part_id: number;
  reservation_id: number;
  booking_id: number;
  has_checked_in: boolean;
  first_name: string;
  last_name: string;
  preferred_name?: string;
  birthday: string;
  email: string;
  telephone: string;
  customer_id?: number;
  registration_number: string;
  is_cancelled: boolean;
  is_staff_direct_booking: boolean;
  payment_status: ScheduledEventPaymentStatus;
  payment_reference: number;
  payment_fee_name: string;
  add_on_names: string[];
  add_on_names_display?: string[];
  // This participant's share of their booking's money (PRO-1491). Two-place
  // decimal strings, tax-inclusive; a booking covering several participants is
  // divided among them. Cancelled participants read '0.00'.
  invoiced_amount: string;
  amount_paid: string;
  amount_refunded: string;
  booked_by_id?: number;
  booked_by_first_name?: string;
  booked_by_last_name?: string;
  booked_by_email?: string;
  question_responses: any;
  booking_question_responses: any;

  // The following fields are only present when retrieving an individual
  // scheduled event, for performance reasons. When listing scheduled events,
  // they are undefined.
  linked_private_event_id?: number | null;
  linked_private_event_first_part_id?: number | null;
  // The tokenized, login-free URL for paying off this booking's remaining
  // balance (PRO-1388). Built server-side; empty string unless the booking has
  // an outstanding balance.
  booking_payment_url?: string;
  // Whether this reservation grants facility entry at check-in (PRO-1512).
  includes_entry?: boolean;

  // Client-side variables, not returned from the API.
  checkInLoading?: boolean;
}

/**
 * Detailed reservation + booking info for a single reservation part, returned by
 * the get-reservation-part-details endpoint and used by the participant details
 * modal (PRO-1217). Extends the standard reservation payload with the booking
 * status, timestamps, and the other participants on the same booking.
 */
export interface ReservationPartDetails extends ScheduledEventReservation {
  event_part_id: number;
  booking_status: ReservationBookingStatus;
  cancellation_reason: string;
  created_at: string;
  updated_at: string;
  other_participants: OtherBookingParticipant[];
}

export interface OtherBookingParticipant {
  reservation_part_id: number;
  first_name: string;
  last_name: string;
  customer_id: number | null;
  is_cancelled: boolean;
}

/**
 * The same participant, addressed by reservation rather than by one of its
 * sessions: the reservation details modal links to a whole reservation.
 */
export interface OtherBookingReservation {
  reservation_id: number;
  first_name: string;
  last_name: string;
  customer_id: number | null;
  is_cancelled: boolean;
}

/**
 * A whole reservation (every session), returned by the
 * get-reservation-details endpoint and used by the participant details modal
 * in its reservation mode (PRO-1514). The inherited part-level fields are read
 * off the reservation's FIRST session.
 */
export interface ReservationDetails extends Omit<ReservationPartDetails, 'other_participants'> {
  event_type_name: string;
  event_first_part_id: number | null;
  event_start_datetime: string | null;
  event_end_datetime: string | null;
  time_zone: string;
  event_num_parts: number;
  parts: ReservationDetailsPart[];
  other_participants: OtherBookingReservation[];
}

export interface ReservationDetailsPart {
  reservation_part_id: number;
  event_part_id: number;
  event_part_number: number;
  start_datetime: string;
  end_datetime: string;
  has_checked_in: boolean;
}

/**
 * A whole booking, returned by the get-booking-details endpoint and used by
 * the booking details modal (PRO-1514). Money values are two-place decimal
 * strings; datetimes are UTC and are formatted in `time_zone`.
 */
export interface BookingDetails {
  id: number;
  status: BookingStatus;
  is_cancelled: boolean;
  created_at: string;
  updated_at: string;

  event_id: number;
  event_type_name: string;
  event_first_part_id: number | null;
  event_start_datetime: string | null;
  event_end_datetime: string | null;
  time_zone: string;
  event_num_parts: number;
  location_name: string;
  is_private: boolean;
  is_private_event_request_form: boolean;

  booking_customer_id: number | null;
  booking_customer_first_name: string | null;
  booking_customer_last_name: string | null;
  booking_customer_email: string | null;

  is_staff_direct_booking: boolean;
  staff_user_first_name: string | null;
  staff_user_last_name: string | null;
  staff_user_email: string | null;

  roster_membership_id: number | null;
  roster_membership_name: string | null;
  linked_private_event_id: number | null;
  linked_private_event_first_part_id: number | null;

  // Joined name plus email, rather than the split fields getStaffNameDisplay
  // takes.
  participant_agreement_bypassed_by_name: string | null;
  participant_agreement_bypassed_by_email: string | null;
  required_waiver_bypassed_by_name: string | null;
  required_waiver_bypassed_by_email: string | null;

  fee_name: string;
  add_on_names: string[];
  add_on_names_display: string[];
  per_booking_base_fee: string;
  per_booking_add_on_fee: string;
  // Null when the event's pricing matrix takes no deposit.
  per_booking_deposit: string | null;

  invoice_id: number;
  invoice_amount: string;
  invoice_tax_amount: string;
  invoice_amount_paid: string;
  invoice_amount_due: string;
  invoice_refunded_amount: string;
  num_vouchers_used: number;
  vouchers_amount_paid: string;
  invoice_payment_type: PaymentType | null;
  invoice_payment_method_type: PaymentMethodType | null;
  completed_payments: BookingDetailsCompletedPayment[];

  question_responses: any;
  custom_customer_field_values: BookingDetailsCustomFieldValue[];
  participants: BookingDetailsParticipant[];
}

export interface BookingDetailsCompletedPayment {
  id: number;
  amount: string;
  // Refunded and partially refunded payments are listed too, so this is often
  // non-zero.
  amount_refunded: string;
  payment_type: PaymentType;
  payment_method_type: PaymentMethodType;
  created_at: string;
}

export interface BookingDetailsCustomFieldValue {
  field_id: number;
  // Null when the field has since been deleted.
  field_name: string | null;
  values: string[];
}

export interface BookingDetailsParticipant {
  reservation_id: number;
  customer_id: number | null;
  first_name: string;
  last_name: string;
  birthday: string | null;
  is_cancelled: boolean;
  checked_in_part_count: number;
  part_count: number;
}

export interface WaitlistEntryAssignedEvent {
  id: number;
  first_start_datetime: string;
  event_part_count: number;
  event_schedule_from_waitlist_url: string;
}

export interface WaitlistEntryAssignedRoster {
  id: number;
  name: string;
}

export interface WaitlistEntry {
  id: string;
  first_name: string;
  last_name: string;
  birthday: string;
  notification_email: string;
  event_type_id: number;
  event_type_name: string;
  status: WaitlistEntryStatus;
  participant_customer_id: number | null;

  // The participant's own email. Only the invitation paths that pass
  // send_invite_to_customer_email deliver to it, but it is one of the
  // addresses an invitation can reach, so the invite modals read it to warn
  // when an invitation would reach nobody at all.
  participant_customer_email: string | null;

  // False when the participant has been removed. Every invitation email helper
  // returns before building a recipient list at all in that case, so no
  // invitation reaches anyone however many addresses the entry carries. Null
  // when the entry has no linked participant.
  participant_customer_is_active: boolean | null;

  // The linked participant's current birthday, which the server's age check
  // prefers over the entry's stored `birthday`. Only some payloads carry it.
  participant_customer_birthday?: string | null;

  booking_customer_id: number | null;
  booking_customer_first_name: string | null;
  booking_customer_last_name: string | null;
  booking_customer_email: string | null;
  booking_customer_telephone: string | null;
  created_at: string;
  updated_at: string;
  waiting_since: string;
  booking_deadline: string | null;
  // IANA zone the booking deadline is set and shown in: the event's or
  // roster's location.
  time_zone: string;

  // The markdown note staff wrote on the invitation, rendered into the
  // invitation email. Kept so re-sending repeats it; blank for entries invited
  // before it was stored, and for invitations sent without one.
  invite_note: string;

  // The start date staff chose for the roster membership when inviting. When
  // null, the customer chooses it at purchase (if the membership allows it),
  // and otherwise the membership starts on the purchase date.
  // Only used for rolling event types.
  start_date: string | null;

  // Only present for event type-wide waitlists.
  preferred_days_of_week?: string;
  preferred_locations?: string;
  event?: WaitlistEntryAssignedEvent;
  roster?: WaitlistEntryAssignedRoster;
}

export interface PrivateEventBooking {
  booked_by_id: number;
  booked_by_first_name: string;
  booked_by_last_name: string;
  booked_by_birthday: string;
  booked_by_email: string;
  payment_fee_name: string;
  add_on_names: string[];
  add_on_names_display?: string[];
  per_booking_base_fee: string;
  per_booking_add_on_fee: string;
  total_fee_pre_tax: string;
  total_fee: string;
  discount_amount: string;
  total_fee_without_discount: string;
  amount_due: string;
  amount_paid: string;
  amount_refunded: string;
  invoice_id: number;
  payment_processor_type: PaymentProcessorType;
  payments: PrivateEventBookingPayment[];
  pending_requested_payment: PendingRequestedPayment | null;
  reservations: PrivateEventBookingReservation[];
}

export interface PendingRequestedPayment {
  id: number;
  amount: string;
  is_full_amount: boolean;
  custom_note: string;
  created_at: string;
  // The login-free Climber App payment link, built server-side so staff can
  // copy it to the clipboard and share it themselves.
  payment_url: string;
  has_been_emailed: boolean;
}

export interface PrivateEventBookingReservation {
  id?: number;
  first_name: string;
  last_name: string;
  birthday: string;
  email: string;
  preferred_name?: string;
  customer_id?: number;
  question_responses: {[key: string]: string};
  payment_fee_name: string;
  add_on_names: string[];
  add_on_names_display?: string[];
  per_participant_base_fee: string;
  per_participant_add_on_fee: string;
  confirmation_email_sent: boolean;
  send_confirmation_email?: boolean;
}

export interface PrivateEventBookingPayment {
  amount: string;
  amount_refunded: string;
  date: string;
}

export interface ScheduledEventFilters {
  event_type_id?: number;
  event_category_id?: number;
  is_private?: boolean;
  location_id?: number;
  is_cancelled?: boolean;
  has_instructor?: boolean;
  instructor_id?: number;
  instructor_name?: string;
  booking_capacity?: ScheduledEventFilterBookingCapacity;
  has_waitlist?: boolean;
}

export function loadScheduledEventFiltersFromGetParams(activatedRoute: ActivatedRoute): ScheduledEventFilters {
  const filters: ScheduledEventFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'event_type_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_private', initialQPs);
  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);
  loadBoolFilterFromGetParams(filters, 'is_cancelled', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_instructor', initialQPs);
  loadIntFilterFromGetParams(filters, 'instructor_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'instructor_name', initialQPs);
  loadStringFilterFromGetParams(filters, 'booking_capacity', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_waitlist', initialQPs);

  return filters;
}

/**
 * Represents resources from the /event-waitlist-summaries/ API collection: one
 * row per event, with its Waiting and Pending waitlist entry counts.
 */
export interface EventWaitlistSummary {
  id: number;
  event_type_id: number;
  event_type_name: string;
  event_type_schedule_type: ScheduleType;
  occurrence_name: string;
  is_private: boolean;
  capacity: number;
  location_id: number;
  location_name: string;
  first_start_datetime: string;
  last_end_datetime: string | null;
  time_zone: string;
  event_part_count: number;
  first_event_part_id: number;
  waiting_count: number;
  pending_count: number;
}

export interface EventWaitlistFilters {
  location_id?: number;
  event_type_id?: number;
  first_start_min?: string;
  first_start_max?: string;
  has_active_waitlist?: boolean;
}

export function loadEventWaitlistFiltersFromGetParams(activatedRoute: ActivatedRoute): EventWaitlistFilters {
  const filters: EventWaitlistFilters = {};

  const initialQPs = activatedRoute.snapshot.queryParamMap;

  loadIntFilterFromGetParams(filters, 'location_id', initialQPs);
  loadIntFilterFromGetParams(filters, 'event_type_id', initialQPs);
  loadStringFilterFromGetParams(filters, 'first_start_min', initialQPs);
  loadStringFilterFromGetParams(filters, 'first_start_max', initialQPs);
  loadBoolFilterFromGetParams(filters, 'has_active_waitlist', initialQPs);

  return filters;
}

/**
 * The pro-rated first bill preview for a roster invite (PRO-1458), returned by
 * roster-invite-first-bill/<rosterId>/. The `is_prorated`/`period_*`/day-count
 * fields are only present when the request carried a `start_date` and
 * `prorates` is true.
 */
export interface RosterInviteFirstBill {
  prorates: boolean;
  bill_day_of_month: number | null;
  interval: MembershipInterval | '';
  interval_display: string;
  allow_selecting_start_date: boolean;
  base_fee: string;
  currency: string;

  is_prorated?: boolean;
  period_start_date?: string;
  period_end_date?: string;
  prorated_days?: number;
  total_days?: number;
  percentage?: number;
  prorated_base_fee?: string;
}

export function getWaitlistEntryStatusDisplay(status: WaitlistEntryStatus): string {
  switch (status) {
    case WaitlistEntryStatus.waiting:
      return 'Waiting';
    case WaitlistEntryStatus.pending:
      return 'Pending';
    case WaitlistEntryStatus.accepted:
      return 'Accepted';
    case WaitlistEntryStatus.expired:
      return 'Expired';
    case WaitlistEntryStatus.rejected:
      return 'Rejected';
  }
}
